import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api } from "@/lib/api";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";
import { CATEGORY_PAGES } from "@/pages/DiarioCategoria";
import { useSettings } from "@/context/SettingsContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

export function formatArticleDate(iso) {
  if (!iso) return "";
  const M = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
  const d = new Date(iso);
  return `${d.getDate()} ${M[d.getMonth()]} ${d.getFullYear()}`;
}

export default function Diario() {
  const [cluster, setCluster] = useState("");
  const [limit, setLimit] = useState(9);
  const { settings } = useSettings();
  const { data: cats } = useSWR("/diario/categories", fetcher, { revalidateOnFocus: false });
  const { data, isLoading } = useSWR(
    `/diario/articles?limit=${limit}${cluster ? `&cluster=${cluster}` : ""}`,
    fetcher,
    { revalidateOnFocus: false },
  );
  const items = data?.items || [];
  const [lead, ...rest] = items;

  return (
    <>
      <Seo
        title="Diario dal Borgo · Storie dall'Irpinia · Osteria Aterrana"
        description="Storie, luoghi, prodotti e persone dall'Irpinia: il diario editoriale di Osteria Aterrana, dal borgo di Aterrana a Montoro."
        path="/diario-dal-borgo"
      />
      <PageHeader
        eyebrow="Diario dal Borgo"
        title="Storie, luoghi e prodotti dall'Irpinia"
        lead="Quello che impariamo stando qui: il borgo, i produttori, i vini, le stagioni. Appunti utili anche a chi passa da queste parti."
        label="BORGO PHOTO"
        alt="Il borgo di Aterrana"
        src={settings.borgo_header_image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed">
          {cats?.length > 0 && (
            <nav className="flex flex-wrap gap-x-7 gap-y-3 pb-10 border-b border-wood/15" aria-label="Categorie del diario">
              <button
                type="button"
                onClick={() => setCluster("")}
                className={`eyebrow transition-colors duration-300 ${!cluster ? "text-sage" : "text-wood/45 hover:text-wood"}`}
                data-testid="diario-filter-all"
              >
                Tutte
              </button>
              {cats.filter((c) => c.count > 0).map((c) => (
                CATEGORY_PAGES[c.slug] ? (
                  <Link
                    key={c.slug}
                    to={`/diario-dal-borgo/${c.slug}`}
                    data-testid={`diario-filter-${c.slug}`}
                    className="eyebrow text-wood/45 hover:text-wood transition-colors duration-300"
                  >
                    {c.name}
                  </Link>
                ) : (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setCluster(c.slug)}
                    data-testid={`diario-filter-${c.slug}`}
                    className={`eyebrow transition-colors duration-300 ${cluster === c.slug ? "text-sage" : "text-wood/45 hover:text-wood"}`}
                  >
                    {c.name}
                  </button>
                )
              ))}
            </nav>
          )}

          {isLoading && <p className="mt-10 text-sm text-foreground/55">Carichiamo il diario…</p>}

          {!isLoading && items.length === 0 && (
            <div className="mt-12 border border-dashed border-wood/25 px-6 py-14 text-center" data-testid="diario-empty">
              <p className="font-serif text-2xl text-wood">Il diario sta prendendo forma</p>
              <p className="mt-3 text-sm text-foreground/65">
                I primi articoli sono in preparazione: torna a trovarci tra qualche giorno.
              </p>
            </div>
          )}

          {lead && (
            <Reveal className="mt-12 border-b border-wood/15 pb-14" data-testid="diario-lead">
              <p className="eyebrow text-sage">{lead.category_name}</p>
              <h2 className="mt-5 max-w-[26ch] font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] text-wood">
                <Link to={`/diario-dal-borgo/${lead.slug}`} className="hover:text-sage transition-colors duration-400">
                  {lead.title}
                </Link>
              </h2>
              <p className="mt-6 max-w-2xl text-sm md:text-lg text-foreground/70 leading-relaxed">{lead.excerpt}</p>
              <p className="mt-5 text-xs tracking-wider text-foreground/45">{formatArticleDate(lead.published_at)}</p>
              <Link
                to={`/diario-dal-borgo/${lead.slug}`}
                className="mt-7 inline-flex text-[0.7rem] uppercase tracking-[0.2em] text-sage link-underline"
              >
                Leggi
              </Link>
            </Reveal>
          )}

          {rest.length > 0 && (
            <ul className="mt-14 grid gap-x-12 gap-y-12 md:grid-cols-2 lg:grid-cols-3" data-testid="diario-list">
              {rest.map((a, i) => (
                <Reveal as="li" key={a.id} delay={Math.min(i * 0.05, 0.3)} className="border-t border-wood/15 pt-6">
                  <p className="eyebrow text-gold">{a.category_name}</p>
                  <h3 className="mt-4 font-serif text-2xl leading-snug text-wood">
                    <Link to={`/diario-dal-borgo/${a.slug}`} className="hover:text-sage transition-colors duration-400">
                      {a.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{a.excerpt}</p>
                  <p className="mt-4 text-xs tracking-wider text-foreground/45">{formatArticleDate(a.published_at)}</p>
                </Reveal>
              ))}
            </ul>
          )}

          {data && data.total > items.length && (
            <button
              type="button"
              onClick={() => setLimit(limit + 9)}
              data-testid="diario-load-more"
              className="mt-14 inline-flex px-8 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.2em] hover:bg-wood hover:text-cream transition-colors duration-500"
            >
              Altri articoli
            </button>
          )}
        </div>
      </section>
    </>
  );
}
